import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import ConfirmationModal from '../../components/ConfirmationModal';
import Spinner from '../../components/Spinner';
import './AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    name: '',
    phone: '',
    role: 'customer',
    password: '',
    active: true
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, filterRole, filterStatus]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/login');
      } else {
        showToast('Failed to load users: ' + (error.response?.data?.error || error.message), 'error');
        setLoading(false);
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(term) ||
        u.name?.toLowerCase().includes(term) ||
        u.username?.toLowerCase().includes(term) ||
        u.phone?.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(u => u.role === filterRole);
    }

    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') filtered = filtered.filter(u => u.active === 1);
      if (filterStatus === 'inactive') filtered = filtered.filter(u => u.active === 0);
      if (filterStatus === 'verified') filtered = filtered.filter(u => u.email_verified === 1);
      if (filterStatus === 'unverified') filtered = filtered.filter(u => u.email_verified === 0);
    }

    setFilteredUsers(filtered);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      username: '',
      name: '',
      phone: '',
      role: 'customer',
      password: '',
      active: true
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username || '',
      name: user.name || '',
      phone: user.phone || '',
      role: user.role,
      password: '', // Don't prefill password
      active: user.active === 1
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email) {
      showToast('Email is required', 'warning');
      return;
    }

    if (!editingUser && !formData.password) {
      showToast('Password is required for new users', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        email: formData.email,
        username: formData.username,
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        active: formData.active ? 1 : 0
      };

      // Only include password if provided
      if (formData.password) {
        payload.password = formData.password;
      }

      if (editingUser) {
        // Update existing user
        await axios.put(`/api/admin/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new user
        await axios.post('/api/admin/users', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      fetchUsers();
      showToast('User saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save user: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const toggleActive = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/admin/users/${userId}`, {
        active: currentStatus ? 0 : 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      showToast('User status updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update user status: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const confirmDeleteUser = (userId, userEmail) => {
    setDeleteConfirm({ userId, userEmail });
  };

  const deleteUser = async () => {
    if (!deleteConfirm) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/admin/users/${deleteConfirm.userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchUsers();
      showToast('User deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete user: ' + (error.response?.data?.error || error.message), 'error');
      setDeleteConfirm(null);
    }
  };

  if (loading) return <Spinner text="Loading users..." />;

  return (
    <div className="admin-users">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="page-header">
        <h1>Manage Users</h1>
      </div>

      <div className="users-actions">
        <button onClick={openCreateModal} className="btn-primary">
          + Create New User
        </button>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by email, name, username, or phone..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="filters-row">
          <select
            className="filter-select"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admins</option>
            <option value="customer">Customers</option>
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="verified">Email Verified</option>
            <option value="unverified">Email Unverified</option>
          </select>
        </div>

        <div className="results-count">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Name</th>
              <th>Username</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td className="email-cell">{user.email}</td>
                <td>{user.name || '-'}</td>
                <td>{user.username || '-'}</td>
                <td>{user.phone || '-'}</td>
                <td>
                  <span className={`badge ${user.role === 'admin' ? 'badge-danger' : 'badge-info'}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <div className="status-badges">
                    <span className={`badge ${user.active ? 'badge-success' : 'badge-warning'}`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                    {user.email_verified === 1 && (
                      <span className="badge badge-verified">Verified</span>
                    )}
                  </div>
                </td>
                <td className="date-cell">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-action btn-edit"
                      onClick={() => openEditModal(user)}
                    >
                      Edit
                    </button>
                    <button
                      className={`btn-action ${user.active ? 'btn-deactivate' : 'btn-activate'}`}
                      onClick={() => toggleActive(user.id, user.active)}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => confirmDeleteUser(user.id, user.email)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-results">
            No users found matching your filters.
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Optional - leave blank to auto-generate"
                />
              </div>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="customer">Customer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete User"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={deleteUser}>
              Delete
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete user "{deleteConfirm?.userEmail}"?</p>
        <p><strong>This action cannot be undone.</strong></p>
      </ConfirmationModal>
    </div>
  );
}

export default AdminUsers;
